import { useState } from "react";
import * as UTIF from "utif";
import type {
  WhiteLayerExportPayload,
  WhiteLayerExportResponse,
  WhiteLayerProcessedImagePayload,
} from "../types/whiteLayer";
import { validateExportPayload } from "../utils/export/exportValidation";

type UseWhiteLayerExportResult = {
  exportWhiteLayer: (
    payload: WhiteLayerExportPayload
  ) => Promise<WhiteLayerExportResponse>;
  isExporting: boolean;
  exportError: string | null;
  clearExportError: () => void;
};

function triggerDownloadFromBlob(blob: Blob, fileName: string) {
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
}

function base64ToUint8Array(base64Data: string): Uint8Array {
  const byteCharacters = atob(base64Data);
  const bytes = new Uint8Array(byteCharacters.length);

  for (let i = 0; i < byteCharacters.length; i += 1) {
    bytes[i] = byteCharacters.charCodeAt(i);
  }

  return bytes;
}

function toExactArrayBuffer(
  bytes: Uint8Array | Uint8ClampedArray | ArrayBufferLike
): ArrayBuffer {
  if (bytes instanceof Uint8Array || bytes instanceof Uint8ClampedArray) {
    const view = new Uint8Array(
      bytes.buffer,
      bytes.byteOffset,
      bytes.byteLength
    );
    const copy = new Uint8Array(view.byteLength);
    copy.set(view);
    return copy.buffer;
  }

  if (bytes instanceof ArrayBuffer) {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(new Uint8Array(bytes));
    return copy.buffer;
  }

  const view = new Uint8Array(bytes);
  const copy = new Uint8Array(view.byteLength);
  copy.set(view);
  return copy.buffer;
}

function buildBaseFileName(fileName: string): string {
  return fileName.replace(/\.[^/.]+$/, "");
}

function buildExportFileName(
  fileName: string,
  exportFormat: "png" | "tiff"
): string {
  const baseName = buildBaseFileName(fileName);
  return exportFormat === "tiff"
    ? `${baseName}_CMYK_W1.tif`
    : `${baseName}_W1.png`;
}

function buildRgbaFromGrayMask(
  grayBytes: Uint8Array,
  width: number,
  height: number
): Uint8Array {
  const expectedLength = width * height;

  if (grayBytes.length !== expectedLength) {
    throw new Error(
      `W1 mask buffer length mismatch. Expected ${expectedLength} bytes but received ${grayBytes.length}.`
    );
  }

  const rgba = new Uint8Array(expectedLength * 4);

  for (
    let srcIndex = 0, destIndex = 0;
    srcIndex < grayBytes.length;
    srcIndex += 1, destIndex += 4
  ) {
    const gray = grayBytes[srcIndex];
    rgba[destIndex] = gray;
    rgba[destIndex + 1] = gray;
    rgba[destIndex + 2] = gray;
    rgba[destIndex + 3] = 255;
  }

  return rgba;
}

function loadImageFromBase64(base64: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Failed to load original image for TIFF export."));
    image.src = `data:image/png;base64,${base64}`;
  });
}

async function exportProcessedMaskAsPng(
  payload: WhiteLayerProcessedImagePayload
): Promise<WhiteLayerExportResponse> {
  try {
    const grayBytes = base64ToUint8Array(payload.w1MaskBase64);
    const rgba = buildRgbaFromGrayMask(grayBytes, payload.width, payload.height);

    const canvas = document.createElement("canvas");
    canvas.width = payload.width;
    canvas.height = payload.height;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      return {
        success: false,
        error: "Could not create export canvas context.",
      };
    }

    const imageData = new ImageData(
      new Uint8ClampedArray(rgba),
      payload.width,
      payload.height
    );

    ctx.putImageData(imageData, 0, 0);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, "image/png");
    });

    if (!blob) {
      return {
        success: false,
        error: "Failed to encode white layer PNG.",
      };
    }

    const fileName = buildExportFileName(payload.fileName, "png");
    triggerDownloadFromBlob(blob, fileName);

    return {
      success: true,
      fileName,
      mimeType: "image/png",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to prepare PNG export.",
    };
  }
}

async function exportProcessedMaskAsTiff(
  payload: WhiteLayerProcessedImagePayload
): Promise<WhiteLayerExportResponse> {
  try {
    const grayBytes = base64ToUint8Array(payload.w1MaskBase64);
    const image = await loadImageFromBase64(payload.originalImageBase64);

    const canvas = document.createElement("canvas");
    canvas.width = payload.width;
    canvas.height = payload.height;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      return {
        success: false,
        error: "Could not create TIFF export canvas context.",
      };
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(image, 0, 0, payload.width, payload.height);

    const originalData = ctx.getImageData(
      0,
      0,
      payload.width,
      payload.height
    ).data;

    if (grayBytes.length !== payload.width * payload.height) {
      return {
        success: false,
        error: "W1 mask length does not match image dimensions.",
      };
    }

    const rgba = new Uint8Array(payload.width * payload.height * 4);

    for (let i = 0; i < grayBytes.length; i += 1) {
      const idx = i * 4;

      rgba[idx] = originalData[idx];
      rgba[idx + 1] = originalData[idx + 1];
      rgba[idx + 2] = originalData[idx + 2];

      // W1 carried in alpha channel
      rgba[idx + 3] = grayBytes[i];
    }

    const encoded = UTIF.encodeImage(rgba, payload.width, payload.height);
    const tiffArrayBuffer = toExactArrayBuffer(encoded);
    const fileName = buildExportFileName(payload.fileName, "tiff");
    const blob = new Blob([tiffArrayBuffer], { type: "image/tiff" });

    triggerDownloadFromBlob(blob, fileName);

    return {
      success: true,
      fileName,
      mimeType: "image/tiff",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : "Failed to prepare TIFF export.",
    };
  }
}

export function useWhiteLayerExport(): UseWhiteLayerExportResult {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const clearExportError = () => {
    setExportError(null);
  };

  const exportWhiteLayer = async (
    payload: WhiteLayerExportPayload
  ): Promise<WhiteLayerExportResponse> => {
    setIsExporting(true);
    setExportError(null);

    try {
      const validation = validateExportPayload(payload);

      if (!validation.valid) {
        const message = validation.errors.join(" ");
        setExportError(message);

        return {
          success: false,
          error: message,
        };
      }

      if (payload.sourceType !== "processed-image") {
        const message =
          "This export flow currently supports processed-image payloads only.";
        setExportError(message);

        return {
          success: false,
          error: message,
        };
      }

      const result =
        payload.exportFormat === "tiff"
          ? await exportProcessedMaskAsTiff(payload)
          : await exportProcessedMaskAsPng(payload);

      if (!result.success && result.error) {
        setExportError(result.error);
      }

      return result;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unknown export error while preparing white layer export.";

      setExportError(message);

      return {
        success: false,
        error: message,
      };
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportWhiteLayer,
    isExporting,
    exportError,
    clearExportError,
  };
}